<template>
  <div class="sms-text-box-placeholders-menu-wrapper flex items-center">
    <span
      ref="menuTrigger"
      class="menu-trigger">
      <a
        class="attribute-option-text flex items-center link-plain"
        @click.prevent.stop="toggleMenu">
        <feather-icon
          icon="LogInIcon"
          svgClasses="w-5 h-4" />
        <span class="ml-1 hidden md:inline-block">{{ title || $t('InsertMessagePlaceHolders') }}</span>
      </a>
    </span>

    <portal
      v-if="showMenu"
      to="app-portal-target">
      <div
        ref="menu"
        class="v-nav-menu sms-text-box-placeholders-menu"
        :style="menuStyle">
        <template v-for="(item, index) in menuItems">
          <v-nav-menu-item
            v-if="!item.submenu"
            :key="`item-${index}`"
            :index="index"
            :to="item.slug !== 'external' ? item.url : null"
            :href="item.slug === 'external' ? item.url : null"
            :icon="item.icon"
            :icon-pack="item.iconPack"
            :feather-icon="item.featherIcon"
            :target="item.target"
            :isDisabled="item.isDisabled"
            :slug="item.slug"
            class="cursor-pointer"
            @click-item="insertPlaceholder(item)"
            @redirected="$emit('redirected')">
            <span class="truncate">
              {{ item.name }}
            </span>
          </v-nav-menu-item>

          <!-- Nav-Group -->
          <template v-else>
            <v-nav-menu-group
              :key="`group-${index}`"
              :openHover="false"
              :group="item"
              :groupIndex="index"
              :open="false"
              items-pointer
              @click-item="insertPlaceholder($event)"
              @redirected="$emit('redirected')"/>
          </template>
        </template>
      </div>
    </portal>
  </div>

</template>

<script>
import VNavMenuGroup from '@/layouts/components/vertical-nav-menu/VerticalNavMenuGroup.vue';
import VNavMenuItem from '@/layouts/components/vertical-nav-menu/VerticalNavMenuItem.vue';
import {mapGetters} from 'vuex';

export default {
  name: 'SMSTextBoxPlaceholdersMenu',
  components: { VNavMenuItem, VNavMenuGroup },
  i18n: {
    messages: {
      en: {
        InsertMessagePlaceHolders: 'Insert message placeholders',
        InsertContactsAttributes: 'Contact attributes',
        InsertBusinessDetailsList: 'Business details',
        InsertWaitingList: 'Waitlist',
        InsertReviewLink: 'Review link',
        InsertBusinessName: 'Business name',
      },
    },
  },
  props: {
    allContactAttrs: {
      type: Array,
      required: false,
    },
    isWaitingListMessage: {
      type: Boolean,
      required: false,
      default: false,
    },
    isWritePolicy: {
      type: Boolean,
      required: false,
      default: false,
    },
    title: {
      type: String,
      required: false,
    },
  },
  data() {
    return {
      showMenu: false,
      activeGroup: null,
      menuItems: [],
      menuStyle: {},
    };
  },
  computed: {
    ...mapGetters({
      tenantAccountDetails: 'auth/tenantAccountDetails',
    }),
  },
  created() {
    this.setMenuItems();
  },
  mounted() {
    document.addEventListener('click', this.closeMenu);
    window.addEventListener('resize', this.handleResize);
    window.addEventListener('scroll', this.handleScroll);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closeMenu);
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('scroll', this.handleScroll);
  },
  watch: {
    allContactAttrs() {
      this.setMenuItems();
    },
    tenantAccountDetails() {
      this.setMenuItems();
    },
  },
  methods: {
    setMenuItems() {
      this.menuItems = [];

      if (!this.isWritePolicy) {
        this.menuItems = [
          ...this.menuItems,
          {
            url: null,
            name: this.$t('InsertContactsAttributes'),
            icon: 'UserIcon',
            submenu: [
              ...this.allContactAttrs.map((attr) => ({
                ...attr,
                url: null,
              })),
            ],
          },
        ];
      }

      this.menuItems = [
        ...this.menuItems,
        {
          url: null,
          name: this.$t('InsertBusinessDetailsList'),
          icon: 'BriefcaseIcon',
          submenu: Object.entries(this.$enums.Campaign.BusinessPlaceholders)
            .map(([key, label]) => {
              const attributePath = this.$enums.Campaign.BusinessPlaceholdersAttributeName[key];
              const value = attributePath.split('.').reduce((obj, k) => obj?.[k], this.tenantAccountDetails);
              const isEmpty = !value;

              return {
                url: null,
                name: label,
                shorthand: `[${label}]`,
                attribute: attributePath,
                type: this.$enums.Attributes.Type.BUSINESS,
                isDisabled: isEmpty,
                actionIcon: isEmpty ? 'PlusIcon' : null,
                actionRoute: isEmpty ? { name: 'user-account-details' } : null,
              };
            }),
        },
      ];

      if (this.isWaitingListMessage) {
        this.menuItems = [
          ...this.menuItems,
          {
            url: null,
            name: this.$t('InsertWaitingList'),
            icon: 'UsersIcon',
            submenu: Object.values(this.$enums.Campaign.WaitListPlaceholders)
              .map((attr) => ({
                url: null,
                name: attr,
                shorthand: `[${attr}]`,
                attribute: attr,
                type: this.$enums.Attributes.Type.WAITLIST,
              })),
          },
        ];
      }

      this.menuItems = [
        ...this.menuItems,
        {
          url: null,
          name: this.$t('InsertReviewLink'),
          icon: 'StarIcon',
          shorthand: `[${this.$enums.Campaign.SpecialPlaceholders.REVIEW_LINK}]`,
          attribute: this.$enums.Campaign.SpecialPlaceholders.REVIEW_LINK,
          type: this.$enums.Attributes.Type.SPECIAL,
        },
      ];
    },
    toggleMenu() {
      this.showMenu = !this.showMenu;

      this.$nextTick(() => {
        if (this.showMenu) {
          this.calculateMenuPosition();
        }
      });
    },
    calculateMenuPosition() {
      const { menuTrigger } = this.$refs;

      if (menuTrigger) {
        const rect = menuTrigger.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const scrollX = window.scrollX;
        const scrollY = window.scrollY;

        // Menu dimensions (approximate)
        const menuWidth = 250;
        const menuHeight = 300;

        let top = rect.bottom + scrollY;
        let left = rect.left + scrollX;

        // Check if menu would go off-screen horizontally
        if (left + menuWidth > viewportWidth) {
          left = viewportWidth - menuWidth - 10; // 10px padding from edge
        }

        // Ensure menu doesn't go off-screen to the left
        if (left < 10) {
          left = 10;
        }

        // Check if menu would go off-screen vertically
        if (top + menuHeight > viewportHeight + scrollY) {
          // Position above the trigger instead
          top = rect.top + scrollY - menuHeight - 5;
        }

        // Ensure menu doesn't go off-screen at the top
        if (top < scrollY + 10) {
          top = scrollY + 10;
        }

        this.menuStyle = {
          position: 'absolute',
          top: `${top}px`,
          left: `${left}px`,
          zIndex: 9999,
        };
      }
    },
    closeMenu(event) {
      if (this.showMenu) {
        const { menu } = this.$refs;

        if (menu && !menu.contains(event.target)) {
          this.showMenu = false;
        }
      }
    },
    insertPlaceholder(placeholder) {
      this.$emit('insert', placeholder);
      // Close menu after inserting placeholder
      this.showMenu = false;
    },
    handleResize() {
      if (this.showMenu) {
        this.calculateMenuPosition();
      }
    },
    handleScroll() {
      if (this.showMenu) {
        this.calculateMenuPosition();
      }
    },
  },
};
</script>

<style scoped lang="scss">
.sms-text-box-placeholders-menu-wrapper {
  position: relative;

  .menu-trigger {
    display: inline-block;
    cursor: pointer;
  }
}
</style>

<style lang="scss">
.v-nav-menu.sms-text-box-placeholders-menu {
  position: absolute;
  background-color: white;
  border: 1px solid #ddd;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border-radius: 8px;
  padding: 8px;
  z-index: 54000 !important;
  min-width: 250px;
  max-width: calc(100vw - 20px); // Prevent overflow on small screens
  max-height: 60vh; // Use viewport height for better mobile experience
  overflow: auto;
  width: fit-content;

  // Mobile-specific styles
  @media (max-width: 767px) {
    min-width: 280px;
    max-width: calc(100vw - 20px);
    max-height: 50vh;
    font-size: 14px;

    // Ensure menu items are touch-friendly
    .v-nav-menu-item {
      padding: 12px 16px;
      min-height: 44px; // Minimum touch target size

      &:hover {
        background-color: #f8f9fa;
      }
    }

    // Adjust submenu styling for mobile
    .v-nav-menu-group {
      .v-nav-menu-item {
        padding-left: 24px;
      }
    }
  }

  // Tablet and desktop styles
  @media (min-width: 768px) {
    max-height: 400px;
    min-width: 260px;

    .v-nav-menu-item {
      padding: 8px 12px;
      min-height: 36px;
    }
  }

  // Improve scrolling on mobile
  -webkit-overflow-scrolling: touch;

  // Style scrollbar for webkit browsers
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;

    &:hover {
      background: #a8a8a8;
    }
  }
}

// Add a backdrop for mobile to improve UX
@media (max-width: 767px) {
  .sms-text-box-placeholders-menu-wrapper {
    .menu-backdrop {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: rgba(0, 0, 0, 0.1);
      z-index: 53999;
    }
  }
}
</style>
